import type { ReactNode } from 'react';
import { Link } from '../../router/Link';
import { buttonClass, LockIcon, Notice, Panel, Spinner } from '../../ui';
import { useGate, type GatedFeed } from './model';
import layout from '../../ui/layout.module.css';
import { useCopy } from '../../strings/useCopy';
import styles from './gated.module.css';

/**
 * The three account-gated destinations (§8, §13.2). Renders props (R13) — `model.ts` decides whether
 * this is a gate or a pass-through.
 *
 * Signed in, it renders **its children** rather than a page of its own: the gate is about the
 * credential and nothing else, so what it protects is the caller's business. That used to always be
 * a placeholder naming the destination; now all three children are real pages. Two states, one
 * route, and no knowledge here of which is which.
 *
 * **The blocked arm returns before `children`, and that is what makes §8's "not backed by a local store"
 * true rather than merely intended.** The three pages behind it are hooks that fetch on mount, so keeping
 * them mounted and hidden would put an authenticated request on the wire for a reader with no credential
 * — which is the shape of the bug that has each of those pages *also* gate its own query on
 * `session.facts.signedIn`. Two guards for one property, deliberately: this one is the reason no request
 * is made, and that one is the reason none is made if this component is ever refactored.
 */
export function GatedView({
  feed,
  signInHref,
  children,
}: {
  readonly feed: GatedFeed;
  readonly signInHref: string;
  readonly children: ReactNode;
}) {
  const gate = useGate({ feed, signInHref });
  const words = useCopy().text.gated;

  if (gate.state === 'unknown') {
    return (
      <div className={layout.page}>
        <div className={styles.waiting} aria-busy>
          <Spinner />
          <p className={styles.detail} role="status">
            {words.checking}
          </p>
        </div>
      </div>
    );
  }

  if (gate.state === 'available') {
    return (
      <>
        {/* A session that works but does not reach everything still gets its sentence, above whatever
            the page itself has to say (`reasonFor`). `layout.wide` rather than `layout.page`: all three
            children are grid pages in the wide frame, and a notice at the narrower maximum would sit
            visibly inside the content it is about. The gate's *own* arms stay `page` — a spinner and a
            sign-in panel are prose, not a grid. */}
        {gate.reason === null ? null : (
          <div className={layout.wide}>
            <Notice tone="caution">
              {gate.reason} <Link href={gate.signInHref}>{words.openSignIn}</Link>.
            </Notice>
          </div>
        )}
        {children}
      </>
    );
  }

  return (
    <div className={layout.page}>
      <Panel as="section" pad="lg" className={styles.panel}>
        <p className={styles.lock} aria-hidden="true">
          <LockIcon />
        </p>
        <h1 className={styles.title}>{gate.title}</h1>
        <p className={styles.detail}>{gate.detail}</p>
        {gate.reason === null ? null : <p className={styles.reason}>{gate.reason}</p>}
        {/*
         * A link rather than a button: the sign-in page is a place, it has a URL, and the reader may
         * want to open it in another tab — the same argument `router/Link.tsx` makes about not
         * dressing a `<button>` as a link. It carries `?next=`, so approving returns here.
         */}
        <Link className={buttonClass({ variant: 'primary', className: styles.cta })} href={gate.signInHref}>
          {words.signIn}
        </Link>
        <p className={styles.aside}>{gate.anonymous}</p>
      </Panel>
    </div>
  );
}
